import * as React from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowUpDown, MoreHorizontal } from 'lucide-react'
import { ListPage } from '@/components/templates/list-page'
import { Alert } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { deleteJSON, getJSON, patchJSON, postJSON, type ApiError } from '@/lib/api'
import { cn } from '@/lib/utils'
import { CampaignWindowFields, PresetAmountsField } from '@/pages/campaign-form-fields'
import { useViewOnly } from '@/components/templates/app-shell'

export interface AdminCampaign {
  id: number
  name: string
  goal: string | null
  goal_cents: number | null
  open_on: string | null
  close_on: string | null
  archived: boolean
  giving_preset_amounts_cents?: number[] | null
}

export function parsePresetAmounts(value: string): number[] {
  if (!value.trim()) return []
  return value.split(',').map((part) => Math.round(Number(part.trim()) * 100))
}

type SortKey = 'name' | 'goal' | 'open_on' | 'close_on'

export function AdminCampaignsPage() {
  const viewOnly = useViewOnly()
  const queryClient = useQueryClient()
  const [search, setSearch] = React.useState('')
  const [filter, setFilter] = React.useState<'all' | 'active' | 'archived'>('all')
  const [sort, setSort] = React.useState<SortKey>('name')
  const [descending, setDescending] = React.useState(false)
  const [createOpen, setCreateOpen] = React.useState(false)
  const query = useQuery({
    queryKey: ['admin-campaigns', search, filter, sort, descending],
    queryFn: () => {
      const params = new URLSearchParams({
        sort,
        direction: descending ? 'descending' : 'ascending',
      })
      if (search.trim()) params.set('search', search.trim())
      if (filter !== 'all') params.set('filter', filter)
      if (filter === 'all') params.set('include_archived', 'true')
      return getJSON<{ campaigns: AdminCampaign[] }>(`/api/v1/campaigns?${params}`)
    },
    retry: false,
  })
  const campaigns = query.data?.campaigns ?? []
  const toggleSort = (key: SortKey) => {
    if (sort === key) setDescending((value) => !value)
    else {
      setSort(key)
      setDescending(false)
    }
  }
  return (
    <>
      <ListPage
        title="Campaigns"
        primaryAction={{ label: 'New campaign', onClick: () => setCreateOpen(true) }}
        search={{ value: search, onChange: setSearch, placeholder: 'Search campaigns…' }}
        toolbar={
          <div className="flex flex-wrap items-center gap-1.5" aria-label="Filter campaigns">
            {(
              [
                ['all', 'All'],
                ['active', 'Active'],
                ['archived', 'Archived'],
              ] as const
            ).map(([value, label]) => (
              <Button
                key={value}
                type="button"
                variant="outline"
                size="sm"
                aria-pressed={filter === value}
                onClick={() => setFilter(value)}
                className={cn(
                  'h-8 rounded-full px-3 text-caption font-normal',
                  filter === value && 'border-foreground/20 bg-accent font-medium',
                )}
              >
                {label}
              </Button>
            ))}
          </div>
        }
        table={{
          rows: campaigns,
          state: query.isLoading ? 'loading' : query.isError ? 'error' : 'ready',
          getRowId: (campaign) => campaign.id,
          getRowLabel: (campaign) => campaign.name,
          onRowOpen: (campaign) =>
            window.location.assign(`/finance/donations/campaigns/${campaign.id}`),
          emptyTitle: search || filter !== 'all' ? 'No matching campaigns' : 'No campaigns yet',
          emptyVariant: 'quiet',
          columns: campaignColumns(
            sort,
            descending,
            toggleSort,
            () => queryClient.invalidateQueries({ queryKey: ['admin-campaigns'] }),
            viewOnly,
          ),
        }}
      />
      <CampaignFormDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onSaved={() => queryClient.invalidateQueries({ queryKey: ['admin-campaigns'] })}
      />
    </>
  )
}

function campaignColumns(
  sort: SortKey,
  descending: boolean,
  toggleSort: (key: SortKey) => void,
  onSaved: () => void,
  viewOnly: boolean,
) {
  const header = (label: string, key: SortKey) => (
    <button
      type="button"
      className="inline-flex items-center gap-1 text-caption font-medium"
      onClick={() => toggleSort(key)}
    >
      {label}
      <ArrowUpDown
        className={cn('h-3 w-3', sort === key && 'text-foreground')}
        aria-hidden="true"
      />
      {sort === key && <span className="sr-only">{descending ? 'descending' : 'ascending'}</span>}
    </button>
  )
  return [
    {
      id: 'name',
      header: header('Campaign', 'name'),
      cell: (campaign: AdminCampaign) => (
        <span className="text-body-sm font-medium">{campaign.name}</span>
      ),
    },
    {
      id: 'goal',
      header: header('Goal', 'goal'),
      cell: (campaign: AdminCampaign) => (
        <span className="text-caption text-muted-foreground">{campaign.goal ?? 'none'}</span>
      ),
    },
    {
      id: 'window',
      header: header('Window', 'open_on'),
      cell: (campaign: AdminCampaign) => (
        <span className="text-caption text-muted-foreground">
          {campaign.open_on ? `${campaign.open_on} – ${campaign.close_on}` : 'none'}
        </span>
      ),
    },
    {
      id: 'status',
      header: 'Status',
      cell: (campaign: AdminCampaign) => (
        <Badge variant={campaign.archived ? 'neutral' : 'success'}>
          {campaign.archived ? 'Archived' : 'Active'}
        </Badge>
      ),
    },
    {
      id: 'actions',
      header: <span className="sr-only">Actions</span>,
      cell: (campaign: AdminCampaign) =>
        viewOnly ? null : <CampaignActions campaign={campaign} onSaved={onSaved} />,
    },
  ]
}

export function CampaignActions({
  campaign,
  onSaved,
}: {
  campaign: AdminCampaign
  onSaved: () => void
}) {
  const [deletionError, setDeletionError] = React.useState<string | null>(null)
  const transition = useMutation({
    mutationFn: () =>
      patchJSON(
        `/api/v1/campaigns/${campaign.id}/${campaign.archived ? 'restore' : 'archive'}`,
        {},
      ),
    onSuccess: onSaved,
  })
  const deletion = useMutation({
    mutationFn: () => deleteJSON(`/api/v1/campaigns/${campaign.id}`),
    onSuccess: onSaved,
    onError: (value: ApiError) =>
      setDeletionError(
        value.status === 422
          ? 'This Campaign has references. Archive it instead.'
          : `Campaign could not be deleted: ${value.message}`,
      ),
  })

  return (
    <div className="flex flex-col items-end gap-1">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-8 w-8 px-0"
            aria-label={`More actions for ${campaign.name}`}
          >
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onSelect={() => transition.mutate()}>
            {campaign.archived ? 'Unarchive campaign' : 'Archive campaign'}
          </DropdownMenuItem>
          {!campaign.archived && (
            <DropdownMenuItem
              className="text-destructive"
              onSelect={() => {
                if (window.confirm(`Delete ${campaign.name}?`)) deletion.mutate()
              }}
            >
              Delete campaign
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      {deletionError && <Alert className="max-w-xs text-left">{deletionError}</Alert>}
    </div>
  )
}

// The dialog owns create/edit form state and its conditional fields; keep this component cohesive.
// eslint-disable-next-line complexity
export function CampaignFormDialog({
  campaign,
  open,
  onOpenChange,
  onSaved,
}: {
  campaign?: AdminCampaign
  open: boolean
  onOpenChange: (open: boolean) => void
  onSaved: () => void
}) {
  const [name, setName] = React.useState(campaign?.name ?? '')
  const [goal, setGoal] = React.useState(
    campaign?.goal_cents ? (campaign.goal_cents / 100).toFixed(2) : '',
  )
  const [openOn, setOpenOn] = React.useState(campaign?.open_on ?? '')
  const [closeOn, setCloseOn] = React.useState(campaign?.close_on ?? '')
  const [presets, setPresets] = React.useState(
    (campaign?.giving_preset_amounts_cents ?? [])
      .map((cents) => (cents / 100).toFixed(2))
      .join(', '),
  )
  const [error, setError] = React.useState<string | null>(null)
  React.useEffect(() => {
    if (!open) return
    setName(campaign?.name ?? '')
    setGoal(campaign?.goal_cents ? (campaign.goal_cents / 100).toFixed(2) : '')
    setOpenOn(campaign?.open_on ?? '')
    setCloseOn(campaign?.close_on ?? '')
    setPresets(
      (campaign?.giving_preset_amounts_cents ?? [])
        .map((cents) => (cents / 100).toFixed(2))
        .join(', '),
    )
    setError(null)
  }, [campaign, open])
  const mutation = useMutation({
    mutationFn: () =>
      campaign
        ? patchJSON(`/api/v1/campaigns/${campaign.id}`, {
            campaign: {
              name,
              goal: goal || null,
              open_on: openOn || null,
              close_on: closeOn || null,
              giving_preset_amounts_cents: parsePresetAmounts(presets),
            },
          })
        : postJSON('/api/v1/campaigns', { campaign: { name } }),
    onSuccess: () => {
      onOpenChange(false)
      onSaved()
    },
    onError: (value: ApiError) => setError(value.message),
  })
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{campaign ? 'Edit campaign' : 'New campaign'}</DialogTitle>
          <DialogDescription>
            {campaign
              ? 'Update the campaign details.'
              : 'Start with a name. You can add a goal and window later.'}
          </DialogDescription>
        </DialogHeader>
        {error && <Alert>{error}</Alert>}
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="campaign-name">Name</Label>
            <Input
              id="campaign-name"
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
          </div>
          {campaign && (
            <>
              <div className="space-y-1.5">
                <Label htmlFor="campaign-goal">Goal</Label>
                <Input
                  id="campaign-goal"
                  inputMode="decimal"
                  placeholder="Optional"
                  value={goal}
                  onChange={(event) => setGoal(event.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <PresetAmountsField value={presets} onChange={setPresets} />
              </div>
              <div className="space-y-1.5">
                <CampaignWindowFields
                  openOn={openOn}
                  closeOn={closeOn}
                  onOpenChange={setOpenOn}
                  onCloseChange={setCloseOn}
                />
              </div>
            </>
          )}
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            disabled={!name.trim() || mutation.isPending}
            onClick={() => mutation.mutate()}
          >
            {mutation.isPending ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
